import { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import {
  HomePageStarredEntities,
  HomePageToolkit,
  HomePageTopVisited,
  HomePageRecentlyVisited,
} from '@backstage/plugin-home';
import { SearchBar, SearchContextProvider, useSearch } from '@backstage/plugin-search-react';
import { useNavigate } from 'react-router-dom';
import { identityApiRef, ProfileInfo, useApi } from '@backstage/core-plugin-api';

// Inner component to use search context
const HomeSearchBar = ({ classes }: { classes: Record<string, string> }) => {
  const navigate = useNavigate();
  const { term } = useSearch();

  const handleSubmit = () => {
    navigate(`/search?query=${encodeURIComponent(term)}`);
  };

  return (
    <div className={classes.searchBarContainer}>
      <SearchBar
        InputProps={{
          classes: {
            root: classes.searchBarInput,
            notchedOutline: classes.searchBarOutline,
          },
        }}
        placeholder="Search"
        onSubmit={handleSubmit}
      />
    </div>
  );
};
import { CatalogIcon, DocsIcon } from '@backstage/core-components';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    width: '100%',
    maxWidth: '600px',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50px',
    boxShadow: theme.shadows[1],
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90vw',
      borderRadius: '25px',
    },
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  searchBarContainer: {
    width: '100%',
    padding: theme.spacing(0, 2),
  },
}));


const useGreetingStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 0, 1),
  },
  avatar: {
    width: 56,
    height: 56,
    fontSize: '1.4rem',
    background: 'linear-gradient(135deg, #7df3e1, #0f172a)',
    color: '#0f172a',
    fontWeight: 700,
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  name: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
}));

const WelcomeGreeting = () => {
  const classes = useGreetingStyles();
  const identityApi = useApi(identityApiRef);
  const [profile, setProfile] = useState<ProfileInfo | undefined>();

  useEffect(() => {
    identityApi.getProfileInfo().then(setProfile).catch(() => {});
  }, [identityApi]);

  const displayName = profile?.displayName ?? '...';
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className={classes.root}>
      <Avatar src={profile?.picture} className={classes.avatar}>
        {!profile?.picture && initials}
      </Avatar>
      <div>
        <Typography className={classes.label}>Today's highlights</Typography>
        <Typography className={classes.name}>Hi, {displayName}</Typography>
      </div>
    </div>
  );
};

export const HomePage = () => {
  const classes = useStyles();

  const tools = [
    {
      url: '/catalog',
      label: 'Catalog',
      icon: <CatalogIcon />,
    },
    {
      url: '/docs',
      label: 'Tech Docs',
      icon: <DocsIcon />,
    },
    {
      url: '/create',
      label: 'Create...',
      icon: <AddCircleOutline />,
    },
  ];

  return (
    <SearchContextProvider>
      <Grid container justifyContent="center" spacing={6} style={{ paddingTop: '4rem' }}>
        <Grid container item xs={12} justifyContent="center">
          <WelcomeGreeting />
        </Grid>
        <Grid container item xs={12} justifyContent="center">
          <HomeSearchBar classes={classes} />
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} md={6}>
            <HomePageStarredEntities />
          </Grid>
          <Grid item xs={12} md={6}>
            <HomePageToolkit tools={tools} />
          </Grid>
          <Grid item xs={12} md={6}>
            <HomePageTopVisited />
          </Grid>
          <Grid item xs={12} md={6}>
            <HomePageRecentlyVisited />
          </Grid>
        </Grid>
      </Grid>
    </SearchContextProvider>
  );
};